import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { RefreshCw } from "lucide-react"
import { useEffect, useMemo } from "react"
import { z } from "zod"

import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"

import { Button } from "@/components/button"
import { PublishedRosterDashboard } from "@/components/guild-war/published-roster-dashboard"
import {
  RosterCompositionCard,
  collectBoardMemberIds,
} from "@/components/guild-war/roster-composition-card"
import {
  RosterEmptyState,
  RosterErrorState,
  RosterLoadingState,
} from "@/components/guild-war/roster-empty-states"
import { RosterExportManager } from "@/components/guild-war/roster-export-manager"
import { RosterHeroCard } from "@/components/guild-war/roster-hero-card"
import {
  buildViewerRosterSummary,
  findViewerTeamRole,
  RosterViewerAssignmentCard,
} from "@/components/guild-war/roster-viewer-assignment-card"
import { StatusAlert } from "@/components/shared/status-alert"
import { Card, CardContent } from "@/components/ui/card"
import { formatGuildDateTime } from "@/lib/guild-date-format"
import {
  guildWarApi,
  type GuildWarMemberProfileIdentity,
  type GuildWarRosterBoardRecord,
} from "@/lib/guild-war-api"
import { memberDisplayLabel } from "@/lib/member-display-label"
import { guildWarKeys } from "@/lib/query-keys"
import {
  createRosterPageUiStore,
  useRosterRouteStore,
} from "@/lib/stores/roster-page-ui-store"

const rosterSearchSchema = z.object({
  rosterDate: z.preprocess(
    (value) => sanitizeRosterDate(value),
    z.string().optional(),
  ),
})

export const Route = createFileRoute("/_authed/guild-war-roster")({
  validateSearch: rosterSearchSchema,
  beforeLoad: () => ({
    rosterStore: createRosterPageUiStore(),
  }),
  component: GuildWarRosterPage,
})

function GuildWarRosterPage() {
  const { auth } = Route.useRouteContext()
  const { rosterDate } = Route.useSearch()
  const navigate = Route.useNavigate()
  const store = useRosterRouteStore()
  const selectedRosterDate = rosterDate ?? ""

  useEffect(() => {
    return () => {
      store.getState().dispose()
    }
  }, [store])

  const publishedRosterQuery = useQuery({
    queryKey: guildWarKeys.publishedRoster(selectedRosterDate),
    queryFn: () => guildWarApi.getPublishedRosterBoard(selectedRosterDate),
    enabled: Boolean(selectedRosterDate),
    retry: false,
  })

  const publishedRecord = publishedRosterQuery.data ?? null
  const publishedMemberIds = useMemo(() => {
    if (!publishedRecord) {
      return []
    }

    return [
      ...(publishedRecord.published
        ? collectBoardMemberIds(publishedRecord.published)
        : []),
      ...(publishedRecord.publishedBy ? [publishedRecord.publishedBy] : []),
    ]
  }, [publishedRecord])

  const publishedMembersQuery = useQuery({
    queryKey: guildWarKeys.publishedRosterMembers(publishedMemberIds),
    queryFn: () =>
      guildWarApi.listGuildWarMemberIdentitiesByIds(publishedMemberIds),
    enabled: publishedMemberIds.length > 0,
    placeholderData: (previous) => previous,
  })

  const publishedMemberById = useMemo(() => {
    const map = new Map<string, GuildWarMemberProfileIdentity>()
    for (const member of publishedMembersQuery.data ?? []) {
      map.set(member.memberId, member)
    }
    return map
  }, [publishedMembersQuery.data])

  const candidateById = useMemo(
    () => buildPublishedCandidateMap(publishedRecord, publishedMemberById),
    [publishedMemberById, publishedRecord],
  )

  const publishedByLabel = useMemo(() => {
    const publishedById = publishedRecord?.publishedBy
    if (!publishedById) {
      return null
    }

    const member = publishedMemberById.get(publishedById)
    const displayName = member?.displayName?.trim() ?? null

    if (!displayName) {
      return null
    }

    return memberDisplayLabel({
      displayName,
      memberId: publishedById,
    })
  }, [publishedMemberById, publishedRecord?.publishedBy])

  const publishedSummary = useMemo(() => {
    if (!publishedRecord?.publishedAt) {
      return {
        state: "empty" as const,
        text: selectedRosterDate
          ? "No published roster for the selected date yet"
          : "Choose a war date to view a published roster",
      }
    }

    if (publishedRecord.publishedBy && !publishedByLabel) {
      return {
        state: "loading" as const,
        text: "Resolving publication details...",
      }
    }

    return {
      state: "ready" as const,
      text: `Published ${formatGuildDateTime(publishedRecord.publishedAt)}${publishedRecord.publishedBy ? ` by ${publishedByLabel}` : ""}`,
    }
  }, [publishedByLabel, publishedRecord, selectedRosterDate])

  const viewerSummary = useMemo(() => {
    if (!auth.user || !publishedRecord?.published) {
      return null
    }

    return buildViewerRosterSummary({
      viewerId: auth.user.userId,
      board: publishedRecord.published,
      candidateById,
    })
  }, [auth.user, candidateById, publishedRecord])

  const viewerTeamRole = useMemo(() => {
    if (!auth.user || !publishedRecord?.published) {
      return null
    }

    return findViewerTeamRole(publishedRecord.published, auth.user.userId)
  }, [auth.user, publishedRecord])

  const exportDateLabel =
    publishedRecord?.publishedDisplayDate || selectedRosterDate || "Guild War"

  return (
    <div className="grid gap-5">
      <RosterHeroCard
        publishedSummary={publishedSummary}
        selectedRosterDate={selectedRosterDate}
        onDateChange={(value) => {
          const nextDate = sanitizeRosterDate(value)

          void navigate({
            search: (prev) => ({
              ...prev,
              rosterDate: nextDate,
            }),
            replace: true,
          })
        }}
        showExportButton={Boolean(publishedRecord?.published)}
        onShowExportPreview={() => store.getState().setShowExportPreview(true)}
        isFetching={
          publishedRosterQuery.isFetching || publishedMembersQuery.isFetching
        }
        onRefresh={() => {
          void publishedRosterQuery.refetch()
          void publishedMembersQuery.refetch()
        }}
      />

      {publishedRosterQuery.isLoading ? <RosterLoadingState /> : null}

      {publishedRosterQuery.isError ? (
        <RosterErrorState
          selectedRosterDate={selectedRosterDate}
          onRetry={() => void publishedRosterQuery.refetch()}
          onClearDate={() =>
            void navigate({
              search: (prev) => ({
                ...prev,
                rosterDate: undefined,
              }),
              replace: true,
            })
          }
        />
      ) : null}

      {!publishedRosterQuery.isError && publishedMembersQuery.isError ? (
        <StatusAlert tone="danger">
          <div className="flex items-center gap-3">
            <span className="flex-1">
              Failed to load roster member details. Some names may appear as
              placeholders.
            </span>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => void publishedMembersQuery.refetch()}
            >
              <RefreshCw className="size-3.5" />
              Retry
            </Button>
          </div>
        </StatusAlert>
      ) : null}

      {!selectedRosterDate &&
      !publishedRosterQuery.isLoading &&
      !publishedRosterQuery.isError &&
      !publishedRecord?.published ? (
        <RosterEmptyState />
      ) : null}

      {publishedRecord?.published ? (
        <>
          <RosterCompositionCard
            store={store}
            board={publishedRecord.published}
          />

          <RosterViewerAssignmentCard viewerSummary={viewerSummary} />

          <Card className="overflow-hidden border-border/60 bg-card/70">
            <CardContent className="overflow-auto p-6">
              <PublishedRosterDashboard
                board={publishedRecord.published}
                candidateById={candidateById}
                viewerTeamKey={viewerTeamRole?.team ?? null}
              />
            </CardContent>
          </Card>

          <RosterExportManager
            store={store}
            board={publishedRecord.published}
            candidateById={candidateById}
            displayDateLabel={exportDateLabel}
          />
        </>
      ) : null}
    </div>
  )
}

function buildPublishedCandidateMap(
  record: GuildWarRosterBoardRecord | null,
  memberById: ReadonlyMap<string, GuildWarMemberProfileIdentity>,
) {
  const map = new Map<string, GuildWarCandidatePoolMember>()

  if (!record?.published) {
    return map
  }

  for (const memberId of collectBoardMemberIds(record.published)) {
    const member = memberById.get(memberId)
    map.set(memberId, {
      memberId,
      guildDisplayName:
        record.published.memberDisplayNames[memberId]?.trim() ||
        member?.displayName?.trim() ||
        "Unknown member",
      permanentStatus: "none",
      source: "weekly",
      availability: "both",
      primaryPathCode: member?.primaryPathCode ?? null,
      secondaryPathCode: member?.secondaryPathCode ?? null,
      absenceState: "none",
      absenceDays: null,
      overlappingAbsences: [],
    })
  }

  return map
}

function sanitizeRosterDate(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return undefined
  }

  return /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : undefined
}
